/**
 * In-viewport preloading of project DETAIL hero images.
 *
 * The rows and grid teasers on the projects page carry data-prefetch-image —
 * a resizer URL template (projects-list.php) whose %w is replaced client-side
 * with the breakpoint the detail page's <img srcset> will actually pick.
 * These heroes used to be warmed on hover (prefetch.js), which never fired on
 * touch devices — where the ~150ms between touchstart and navigation cannot
 * move a hero image. Instead, every project whose row/teaser scrolls into the
 * viewport gets its hero warmed ahead of a possible tap.
 *
 * Runs on ALL engines, unlike prefetch.js: Chromium's touchstart prerender
 * (library/speculative-loading.php) fetches the detail page's eager hero at
 * tap time — from the HTTP cache this module has already filled. Warms via
 * the shared warmQueue's PROJECT bucket (serial, low priority, deduped —
 * and always ahead of team/map/news warms), only after window.load; skipped
 * on Save-Data / slow connections.
 */
import { fastConnection } from './utils'
import { warmImage } from './warmQueue'

// Must match GLUNZ_IMG_BREAKPOINTS (responsive-images.php).
const BREAKPOINTS = [320, 620, 885, 1024, 1280, 1536, 1920]

// Mirror the browser's srcset choice for the detail header image ('1/1'):
// sizes = 100vw at every breakpoint (GLUNZ_IMG_SIZES), times the FULL
// devicePixelRatio — Safari selects with the exact DPR (3 on iPhone Pro), and
// warming any other candidate would fetch a file the browser never uses.
// Keep in sync with the template emitted by projects-list.php.
const heroUrl = (template) => {
  if (!template.includes('%w')) return template // SVG/GIF: original file, no sizes
  const needed = window.innerWidth * (window.devicePixelRatio || 1)
  const width = BREAKPOINTS.find((w) => w >= needed) ?? BREAKPOINTS[BREAKPOINTS.length - 1]
  return template.replace('%w', width)
}

if (fastConnection() && 'IntersectionObserver' in window) {
  const seen = new WeakSet()

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return
        observer.unobserve(entry.target)
        warmImage({ src: heroUrl(entry.target.dataset.prefetchImage) }, 'project')
      })
    },
    // Slight lookahead so a hero starts before its teaser is fully on screen;
    // deliberately smaller than the grid's own 400px batch margin — teaser
    // images stay ahead of speculative hero warms.
    { rootMargin: '100px 0px' }
  )

  const observeNew = (root) =>
    root.querySelectorAll('[data-prefetch-image]').forEach((el) => {
      if (seen.has(el)) return
      seen.add(el)
      observer.observe(el)
    })

  const start = () => {
    observeNew(document)
    // Grid teasers mount client-side in batches (projects.js) after this
    // module runs — watch the grid for them. Re-appended nodes on filter
    // changes are already in `seen`, and re-entering ones dedupe by URL.
    const grid = document.querySelector('[data-projects-grid]')
    if (grid) {
      new MutationObserver(() => observeNew(grid)).observe(grid, { childList: true })
    }
  }

  // After the page's own load: hero warming must never compete with the
  // visible content's images and bundles.
  if (document.readyState === 'complete') start()
  else window.addEventListener('load', start, { once: true })
}
